import { Button, Form, Input, message, Modal } from 'antd';
import { CopyOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { QRCodeCustom } from './QRCodeCustom';
import 'styles/pages/mfa.scss';

/** MultiFactorAuthenticatorModalProps の Props */
type MultiFactorAuthenticatorModalProps = {
    handleClose: () => void;
    setUpData: any;
    isOpen: boolean;
    requestConfirm2FA: (code: string) => void;
    isVerifyMode?: boolean;
    logout?: () => void;
    /** サーバ側でコード確認に失敗したときのエラーメッセージ */
    serverErrorMessage?: string | null;
};

/**
 * 多要素認証の登録モーダル
 * QRコード表示・シークレットコードのコピー・OTPコード入力を行う
 */
const MultiFactorAuthenticatorModal = ({
    handleClose,
    isOpen,
    setUpData,
    requestConfirm2FA,
    isVerifyMode = false,
    logout,
    serverErrorMessage,
}: MultiFactorAuthenticatorModalProps) => {
    const [form] = Form.useForm<{ code: string }>();

    /** フォーム送信ハンドラ */
    const handleFinish = ({ code }: { code: string }) => {
        requestConfirm2FA(code);
    };

    /** キャンセルハンドラ */
    const handleCancel = () => {
        if (!isVerifyMode) {
            form.resetFields();
            handleClose();
        }
    };

    /** クリップボードコピー成功ハンドラ */
    const handleCopy = () => {
        message.success('コードをクリップボードにコピーしました');
    };

    return (
        <Modal
            destroyOnClose
            footer={
                <div>
                    {
                        isVerifyMode ? (
                            <Button onClick={logout}>ログアウト</Button>
                        ) : (
                            <Button onClick={handleCancel}>キャンセル</Button>
                        )
                    }
                    <Button
                        htmlType='submit'
                        onClick={() => form.submit()}
                        type='primary'
                    >
                        確認
                    </Button>
                </div>
            }
            onCancel={handleCancel}
            title={
                <span className='otp-auth-modal-title'>
                    <SafetyCertificateOutlined />
                    多要素認証の登録
                </span>
            }
            visible={isOpen}
            width={760}
        >
            <div
                className='otp-auth-registration-modal'
                style={{
                    paddingLeft: '24px',
                    paddingRight: '24px',
                }}
            >
                {/* QRコード表示エリア */}
                <div className='otp-auth-qrcode-area'>
                    <QRCodeCustom data={setUpData?.otpauth_uri} />
                </div>

                {/* 説明・入力エリア */}
                <div className='otp-auth-input-area'>
                    {/* スキャン案内テキスト */}
                    <p className='otp-auth-scan-label'>
                        認証アプリでQRコードをスキャンしてください
                    </p>

                    {/* QRコードが読み込めない場合の案内テキスト */}
                    <p className='otp-auth-alt-label'>
                        QRコードが読み込めない場合は、下のコードを
                        <br />
                        認証アプリに入力してください。
                    </p>

                    {/* シークレットコード + コピーボタン */}
                    <div className='otp-auth-secret-row'>
                        <Input
                            className='otp-auth-secret-input'
                            readOnly
                            value={setUpData?.secret}
                        />
                        <CopyToClipboard
                            onCopy={handleCopy}
                            text={setUpData?.secret}
                        >
                            <Button
                                className='otp-auth-copy-button'
                                icon={<CopyOutlined />}
                                type='primary'
                            />
                        </CopyToClipboard>
                    </div>

                    {/* OTP入力案内テキスト */}
                    <p className='otp-auth-otp-label'>
                        認証アプリに表示されている6桁のコードを
                        <br />
                        入力して確認ボタンを押して下さい
                    </p>
                    <Form
                        form={form}
                        onFinish={handleFinish}
                    >
                        {/* 6桁OTPコード入力フィールド */}
                        <Form.Item
                            name='code'
                            rules={[
                                {
                                    required: true,
                                    message: '認証コードを入力してください',
                                },
                                {
                                    len: 6,
                                    message: '6桁のコードを入力してください',
                                },
                            ]}
                        >
                            <Input
                                className='otp-auth-otp-input'
                                maxLength={6}
                                placeholder='123456'
                                size='large'
                            />
                        </Form.Item>
                        {/* サーバ側のコード確認失敗時は入力欄直下に赤文字で表示する */}
                        {serverErrorMessage && (
                            <p
                                className='otp-auth-server-error'
                                role='alert'
                                style={{ color: '#ff4d4f', marginTop: '-12px' }}
                            >
                                {serverErrorMessage}
                            </p>
                        )}
                    </Form>
                </div>
            </div>
        </Modal>
    );
};

export default MultiFactorAuthenticatorModal;
